"use client";

import { db } from "@/lib/firebase-client";
import type { AccountTier, UserProfileDocument } from "@/lib/types";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type QueryConstraint,
  type DocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";

const USERS_COLLECTION = "users";
const DEFAULT_PAGE_SIZE = 20;

export type UserSortField = "email" | "displayName" | "createdAt" | "accountTier";
export type UserSortDirection = "asc" | "desc";

export type UserListFilters = {
  search?: string;
  accountTier?: AccountTier;
  isAdmin?: boolean;
  isBetaUser?: boolean;
};

export type UserListOptions = {
  pageSize?: number;
  sortField?: UserSortField;
  sortDirection?: UserSortDirection;
  filters?: UserListFilters;
  startAfterDoc?: DocumentSnapshot<DocumentData>;
};

export type UserListResult = {
  users: UserProfileDocument[];
  lastDoc: DocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  totalCount?: number; // Optional, as counting all users can be expensive
};

/**
 * Get a paginated list of users with optional filtering and sorting
 * Note: This requires admin permissions (enforced by Firestore security rules)
 */
export async function getUsersList(options: UserListOptions = {}): Promise<UserListResult> {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    sortField = "createdAt",
    sortDirection = "desc",
    filters = {},
    startAfterDoc,
  } = options;

  const constraints: QueryConstraint[] = [];

  // Apply filters
  if (filters.accountTier) {
    constraints.push(where("accountTier", "==", filters.accountTier));
  }
  if (filters.isAdmin !== undefined) {
    constraints.push(where("isAdmin", "==", filters.isAdmin));
  }
  if (filters.isBetaUser !== undefined) {
    constraints.push(where("isBetaUser", "==", filters.isBetaUser));
  }

  // Apply sorting
  constraints.push(orderBy(sortField, sortDirection));

  // Apply pagination
  constraints.push(limit(pageSize + 1)); // Fetch one extra to check if there's more

  if (startAfterDoc) {
    constraints.push(startAfter(startAfterDoc));
  }

  const usersQuery = query(collection(db, USERS_COLLECTION), ...constraints);
  const snapshot = await getDocs(usersQuery);

  const docs = snapshot.docs;
  const hasMore = docs.length > pageSize;
  const users = docs
    .slice(0, pageSize)
    .map((doc) => ({
      id: doc.id,
      ...(doc.data() as Omit<UserProfileDocument, "id">),
    }));

  // Note: Client-side search filtering is applied in the component
  // For production, consider implementing server-side search using
  // Firestore's text search capabilities or a search service

  const lastDoc = hasMore ? docs[pageSize - 1] : null;

  return {
    users,
    lastDoc,
    hasMore,
  };
}

/**
 * Get a user profile by ID
 * Note: This requires admin permissions (enforced by Firestore security rules)
 */
export async function getUserById(userId: string): Promise<UserProfileDocument | null> {
  const { doc, getDoc } = await import("firebase/firestore");
  const userRef = doc(db, USERS_COLLECTION, userId);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    return null;
  }

  return {
    id: userSnap.id,
    ...(userSnap.data() as Omit<UserProfileDocument, "id">),
  };
}

